import {useEffect,useRef,useState} from 'react';
import {doc,onSnapshot,serverTimestamp,setDoc} from 'firebase/firestore';
import {db} from '../../Security/firebase-config';
import {defaultCoachDashboard,normalizeCoachDashboard} from './coachDashboardModel';

function cached(key) {try {const value=JSON.parse(localStorage.getItem(key));return value?{...value,config:normalizeCoachDashboard(value.config)}:null;}catch{return null;}}
function cache(key,value) {try {localStorage.setItem(key,JSON.stringify(value));return true;}catch{return false;}}
export default function useCoachDashboard(uid,teamId) {
  const key=`overview.coach.v1.${uid||''}.${teamId||''}`;
  const initial=()=>({key,record:cached(key)||{config:defaultCoachDashboard(),dirty:false,revision:''},status:'loading',error:''});
  const [state,setState]=useState(initial),[retry,setRetry]=useState(0);
  const scope=useRef(key);scope.current=key;
  useEffect(()=>{
    let alive=true;
    const record=cached(key)||{config:defaultCoachDashboard(),dirty:false,revision:''};
    setState({key,record,status:record.dirty?'saving':'loading',error:''});
    if(!uid||!teamId)return undefined;
    const stop=onSnapshot(doc(db,'users',uid,'coachDashboards',teamId),{includeMetadataChanges:true},snapshot=>{
      if(!alive||scope.current!==key)return;
      setState(previous=>{
        if(previous.key!==key)return previous;
        const remote=snapshot.exists()?snapshot.data():null;
        if(previous.record.dirty)return previous;
        if(snapshot.metadata?.fromCache||snapshot.metadata?.hasPendingWrites)return {...previous,status:'local'};
        const next=remote?{config:normalizeCoachDashboard(remote),revision:remote.revision||'',dirty:false}:previous.record;
        cache(key,next);
        return {key,record:next,status:remote?'saved':'ready',error:''};
      });
    },()=>{if(alive&&scope.current===key)setState(previous=>previous.key===key?{...previous,status:'error',error:'Account sync is unavailable. Your changes can still be saved on this device. Retry after updating the dashboard rules or reconnecting.'}:previous);});
    return()=>{alive=false;stop();};
    // Snapshot callbacks are scoped to the signed-in account and active team.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[key,uid,teamId,retry]);
  useEffect(()=>{
    if(state.key!==key||!state.record.dirty||!uid||!teamId)return undefined;
    const record=state.record;
    const timer=setTimeout(()=>{
      setDoc(doc(db,'users',uid,'coachDashboards',teamId),{...record.config,revision:record.revision,editedAt:record.editedAt,updatedAt:serverTimestamp()})
        .then(()=>{
          if(scope.current!==key)return;
          setState(previous=>{
            if(previous.key!==key||previous.record.revision!==record.revision)return previous;
            const saved={...record,dirty:false};cache(key,saved);return {key,record:saved,status:'saved',error:''};
          });
        }).catch(()=>{if(scope.current===key)setState(previous=>previous.key===key&&previous.record.revision===record.revision?{...previous,status:'error',error:'Account sync failed. Your layout is kept on this device; retry to sync it to your other app.'}:previous);});
    },500);
    return()=>clearTimeout(timer);
    // Content edits are coalesced; status-only changes never repeat a write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[key,uid,teamId,state.record.revision,retry]);
  const config=state.key===key?state.record.config:(cached(key)?.config||defaultCoachDashboard());
  function save(value) {
    if(!uid||!teamId)return;
    const record={config:normalizeCoachDashboard(value),dirty:true,editedAt:Date.now(),revision:window.crypto?.randomUUID?.()||`${Date.now()}-${Math.random()}`};
    const local=cache(key,record);
    setState({key,record,status:'saving',error:local?'':'Device storage is unavailable. Keep this page open until account sync finishes.'});
  }
  return {config,save,status:state.key===key?state.status:'loading',error:state.key===key?state.error:'',retry:()=>setRetry(value=>value+1)};
}
